// PATH: src/components/catalog/PlanetPreview.tsx
import * as React from "react";
import { Box, ButtonBase, Tooltip } from "@mui/material";
import { tokens } from "../../theme/theme";

export interface PlanetLook {
  color_primary?: string | null;
  color_secondary?: string | null;
  texture_url?: string | null;
  has_rings?: boolean | null;
}

/**
 * Aperçu indicatif de l'astre dans l'univers 3D élève : couleurs en dégradé,
 * texture plaquée, anneau. Les couleurs viennent des données (pas du thème).
 */
export const PlanetPreview: React.FC<{ look: PlanetLook; size?: number }> = ({ look, size = 88 }) => {
  const c1 = look.color_primary || tokens.coverage.none;
  const c2 = look.color_secondary || look.color_primary || tokens.borderStrong;
  const layers = [
    // éclairage : reflet en haut à gauche, ombre en bas à droite
    "radial-gradient(circle at 32% 28%, rgba(255,255,255,0.55), rgba(255,255,255,0) 42%)",
    "radial-gradient(circle at 70% 75%, rgba(0,0,0,0.35), rgba(0,0,0,0) 60%)",
    look.texture_url ? `url("${look.texture_url}") center / cover` : "",
    `linear-gradient(135deg, ${c1}, ${c2})`,
  ].filter(Boolean);
  return (
    <Box
      aria-hidden
      sx={{ position: "relative", width: size * 1.6, height: size * 1.15, display: "grid", placeItems: "center", flexShrink: 0 }}
    >
      {look.has_rings && (
        <Box
          sx={{
            position: "absolute",
            width: size * 1.55,
            height: size * 0.42,
            borderRadius: "50%",
            border: `${Math.max(3, size / 16)}px solid ${c2}`,
            opacity: 0.55,
            transform: "rotate(-18deg)",
          }}
        />
      )}
      <Box
        sx={{
          position: "relative",
          width: size,
          height: size,
          borderRadius: "50%",
          background: layers.join(", "),
          boxShadow: "inset -6px -8px 18px rgba(0,0,0,0.25), 0 6px 18px rgba(16,24,40,0.12)",
        }}
      />
      {look.has_rings && (
        // moitié avant de l'anneau, par-dessus la planète
        <Box
          sx={{
            position: "absolute",
            width: size * 1.55,
            height: size * 0.42,
            borderRadius: "50%",
            border: `${Math.max(3, size / 16)}px solid ${c2}`,
            borderTopColor: "transparent",
            borderLeftColor: "transparent",
            borderRightColor: "transparent",
            opacity: 0.8,
            transform: "rotate(-18deg)",
          }}
        />
      )}
    </Box>
  );
};

/** Pastille de couleur (matière, programme). */
export const ColorDot: React.FC<{ color?: string | null; size?: number; title?: string }> = ({ color, size = 10, title }) => (
  <Box
    component="span"
    title={title}
    sx={{
      display: "inline-block",
      width: size,
      height: size,
      borderRadius: "50%",
      flexShrink: 0,
      bgcolor: color || "transparent",
      border: color ? "none" : `1.5px dashed ${tokens.borderStrong}`,
      boxShadow: color ? "inset 0 0 0 1px rgba(16,24,40,0.12)" : "none",
    }}
  />
);

/** Couches de fond d'un astre : reflet, ombre, texture, dégradé des couleurs. */
const planetBackground = (look: PlanetLook, fallback?: string | null) => {
  const c1 = look.color_primary || fallback || tokens.coverage.none;
  const c2 = look.color_secondary || look.color_primary || fallback || tokens.borderStrong;
  return [
    "radial-gradient(circle at 32% 28%, rgba(255,255,255,0.55), rgba(255,255,255,0) 45%)",
    "radial-gradient(circle at 70% 75%, rgba(0,0,0,0.3), rgba(0,0,0,0) 60%)",
    look.texture_url ? `url("${look.texture_url}") center / cover` : "",
    `linear-gradient(135deg, ${c1}, ${c2})`,
  ]
    .filter(Boolean)
    .join(", ");
};

/**
 * Petite planète cliquable (liste des matières) : couleurs, texture et anneau
 * de l'astre tel que l'élève le voit. Le clic ouvre l'édition de ses visuels.
 */
export const PlanetButton: React.FC<{
  look: PlanetLook;
  /** Couleur de repli quand l'astre n'a ni couleur ni texture (couleur d'identité). */
  fallbackColor?: string | null;
  label: string;
  onClick: () => void;
  size?: number;
}> = ({ look, fallbackColor, label, onClick, size = 24 }) => {
  const empty = !look.texture_url && !look.color_primary && !fallbackColor;
  return (
    <Tooltip title={`Visuels de ${label} — modifier`}>
      <ButtonBase
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        aria-label={`Modifier les visuels de ${label}`}
        sx={{
          position: "relative",
          width: size + 10,
          height: size + 6,
          borderRadius: 2,
          flexShrink: 0,
          "&:hover .planete, &:focus-visible .planete": { transform: "scale(1.15)" },
          "&:focus-visible": { outline: "2px solid #444CE7", outlineOffset: 1 },
        }}
      >
        {look.has_rings && (
          <Box
            sx={{
              position: "absolute",
              width: size + 10,
              height: size * 0.42,
              borderRadius: "50%",
              border: `2px solid ${look.color_secondary || look.color_primary || tokens.borderStrong}`,
              opacity: 0.7,
              transform: "rotate(-18deg)",
            }}
          />
        )}
        <Box
          className="planete"
          sx={{
            position: "relative",
            width: size,
            height: size,
            borderRadius: "50%",
            transition: "transform .15s",
            ...(empty
              ? { border: `1.5px dashed ${tokens.borderStrong}` }
              : {
                  background: planetBackground(look, fallbackColor),
                  boxShadow: "inset -2px -3px 6px rgba(0,0,0,0.25), 0 1px 3px rgba(16,24,40,0.18)",
                }),
          }}
        />
      </ButtonBase>
    </Tooltip>
  );
};

export default PlanetPreview;
